import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useAuth } from '../auth/AuthProvider.js';

/**
 * Turn an unauthorized answer into a renewed session, once - COW-483.
 *
 * **Every screen needs this, not just the shell.** The first version lived
 * inline in `AppShell` and watched `GET /v1/me` alone, and a review measured
 * what that left: on `/employees`, with `/v1/me` answered from cache and the
 * token lapsing before `/v1/employees`, no refresh was attempted at all. The
 * screen sat on an error card whose "Try again" re-sent the same expired token.
 * Three hooks already computed `isUnauthenticated` and nothing read any of them.
 *
 * A 401 is deliberately not retryable (`retryableStatus`), so nothing re-runs
 * the failed query on its own. That is why this invalidates rather than
 * trusting the cache to notice.
 *
 * The once-only bound lives in `recoverSession`, not here: a screen mounts
 * several of these hooks, and a per-hook guard gave each of them its own turn.
 */
export function useRecoverSession(input: {
  /** The query answered 401 - both envelope shapes, per `use-session.ts`. */
  readonly isUnauthenticated: boolean;
  /**
   * Changes on every fresh error. In the dependency list so a *second* refusal
   * is seen at all: without it the effect fires once and a renewed token that
   * is still refused leaves the app signed in behind an error card for ever.
   */
  readonly erroredAt: number;
}): void {
  const { recoverSession } = useAuth();
  const queryClient = useQueryClient();

  const { isUnauthenticated, erroredAt } = input;

  useEffect(() => {
    if (!isUnauthenticated) return;

    void (async () => {
      if ((await recoverSession()) !== 'renewed') return;

      // Everything, not one key: any query refused with the old token is stale
      // in the same way, and this caller cannot know which others there were.
      //
      // **Order matters and is not obvious.** `invalidateQueries` does not
      // cancel a fetch already in flight and does accept its result - measured -
      // so a request that left carrying the old token could come back 401 after
      // the refresh and trip the exhausted branch on a healthy session. What
      // makes that unreachable is that the store is written inside the refresh's
      // own `.then`, before this line, and a 401'd query is re-sent by nothing
      // except this invalidation: `retryableStatus` refuses to retry a 401 and
      // `refetchOnWindowFocus` is off. Do not reorder these two.
      await queryClient.invalidateQueries();
    })();
  }, [isUnauthenticated, erroredAt, recoverSession, queryClient]);
}
